/**
 * URL에 gzip이 포함되어 있는지 확인
 * @param url - 확인할 URL
 * @returns gzip이 포함되어 있으면 true
 */
export const isGzipUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return url.toLowerCase().includes("gzip");
};

/**
 * DecompressionStream을 사용하여 압축 해제
 * @param bytes - 압축된 바이트 배열
 * @param format - 압축 형식 ('gzip', 'deflate', 'deflate-raw')
 * @returns 압축 해제된 문자열
 */
const decompressWithStream = async (
  bytes: Uint8Array,
  format: "gzip" | "deflate" | "deflate-raw"
): Promise<string> => {
  const stream = new DecompressionStream(format);
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();
  const decoder = new TextDecoder();

  // 압축된 데이터를 스트림에 쓰기
  writer.write(bytes as BufferSource);
  writer.close();

  // 압축 해제된 데이터 읽기
  const chunks: Uint8Array[] = [];
  let done = false;

  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;
    if (value) {
      chunks.push(value);
    }
  }

  // 모든 청크를 하나의 배열로 합치기
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return decoder.decode(result);
};

/**
 * 복호화된 데이터에서 gzip 압축을 해제
 * @param decryptedData - 복호화된 데이터 (문자열)
 * @returns 압축 해제된 데이터, 실패 시 원본 데이터 반환
 */
export const decompressGzipData = async (decryptedData: string): Promise<string> => {
  try {
    let bytes: Uint8Array;

    // 복호화된 데이터를 Uint8Array로 변환
    // 먼저 base64 디코딩 시도
    try {
      const binaryString = atob(decryptedData);
      bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
    } catch {
      // base64 디코딩 실패 시, 원본 문자열을 직접 바이트 배열로 변환
      bytes = new Uint8Array(decryptedData.length);
      for (let i = 0; i < decryptedData.length; i++) {
        bytes[i] = decryptedData.charCodeAt(i);
      }
    }

    // gzip 형식으로 먼저 시도
    try {
      const decompressed = await decompressWithStream(bytes, "gzip");
      console.log("[GZIP_UTILS] :: Gzip decompression successful");
      return decompressed;
    } catch (gzipError) {
      // gzip 실패 시 deflate-raw 시도 (pako.inflate는 기본적으로 raw deflate 사용)
      try {
        const decompressed = await decompressWithStream(bytes, "deflate-raw");
        console.log("[GZIP_UTILS] :: Deflate-raw decompression successful");
        return decompressed;
      } catch (deflateError) {
        // deflate-raw도 실패 시 deflate 시도
        try {
          const decompressed = await decompressWithStream(bytes, "deflate");
          console.log("[GZIP_UTILS] :: Deflate decompression successful");
          return decompressed;
        } catch (allErrors) {
          throw allErrors;
        }
      }
    }
  } catch (gzipError) {
    console.error("[GZIP_UTILS] :: Fail to decompress gzip data", gzipError);
    // 압축 해제 실패 시 원본 데이터 사용
    return decryptedData;
  }
};
